import type { Transaction, TransactionPayload, TransactionType } from "../../lib/types";
import { appToday } from "../../lib/devDate";

export type RecordEditorState =
  | { kind: "create"; initialType?: TransactionType }
  | { kind: "edit"; transaction: Transaction };

export type RecordFormErrors = {
  amount?: string;
  occurred_date?: string;
  account_id?: string;
  transfer_account_id?: string;
};

export function buildRecordPayload(
  form: HTMLFormElement,
  type: TransactionType,
  categoryId: string,
  tagIds: string[],
): { payload?: TransactionPayload; error?: string } {
  const data = new FormData(form);
  const amountText = String(data.get("amount") || "").trim().replace(",", ".");
  const currency = String(data.get("currency") || "").trim().toUpperCase();
  const accountCurrency = String(data.get("account_currency") || "").trim().toUpperCase();
  const accountAmountText = String(data.get("account_amount_preview") || "").trim().replace(",", ".");
  const accountAmountOverride = String(data.get("account_amount_override") || "") === "true";
  const amount = Number(amountText);
  if (!amountText || !Number.isFinite(amount)) return { error: "Amount must be a number." };
  if (!currency) return { error: "Select a currency." };
  if (type === "correction" ? amount === 0 : amount <= 0) {
    return { error: type === "correction" ? "Correction amount cannot be zero." : "Amount must be a positive number." };
  }
  const hasAccountAmountOverride = Boolean(accountAmountOverride && accountCurrency && accountCurrency !== currency);
  const accountAmount = hasAccountAmountOverride ? Number(accountAmountText) : null;
  if (hasAccountAmountOverride && (!accountAmountText || !Number.isFinite(accountAmount))) {
    return { error: "Converted amount must be a number." };
  }
  if (hasAccountAmountOverride && accountAmount !== null && (type === "correction" ? accountAmount === 0 : accountAmount <= 0)) {
    return { error: type === "correction" ? "Converted correction amount cannot be zero." : "Converted amount must be a positive number." };
  }

  const occurredDate = String(data.get("occurred_date") || "");
  if (!occurredDate) return { error: "Select a date." };
  const occurredAt = new Date(`${occurredDate}T12:00:00`);
  if (Number.isNaN(occurredAt.getTime())) return { error: "Select a valid date." };

  const accountId = String(data.get("account_id") || "");
  if (!accountId) return { error: "Select an account." };
  const transferAccountId = String(data.get("transfer_account_id") || "");
  if (type === "transfer" && !transferAccountId) return { error: "Select a destination account." };
  if (type === "transfer" && transferAccountId === accountId) return { error: "Transfer accounts must be different." };

  const payload: TransactionPayload = {
    account_id: accountId,
    type,
    amount: hasAccountAmountOverride && accountAmount !== null ? accountAmount.toFixed(2) : amount.toFixed(2),
    currency: hasAccountAmountOverride ? accountCurrency : currency,
    original_amount: amount.toFixed(2),
    original_currency: currency,
    occurred_at: occurredAt.toISOString(),
  };
  const merchant = String(data.get("merchant") || "").trim();
  const note = String(data.get("note") || "").trim();
  if (type === "transfer") {
    payload.transfer_account_id = transferAccountId;
  } else if (type !== "correction" && categoryId) {
    payload.category_id = categoryId;
  }
  if (type !== "correction") payload.tag_ids = tagIds;
  if (merchant) payload.merchant = merchant;
  if (note) payload.note = note;
  return { payload };
}

export function validateRecordForm(form: HTMLFormElement, type: TransactionType): RecordFormErrors {
  const data = new FormData(form);
  const errors: RecordFormErrors = {};
  const amountText = String(data.get("amount") || "").trim().replace(",", ".");
  const amount = Number(amountText);
  if (!amountText) {
    errors.amount = "Amount is required.";
  } else if (!Number.isFinite(amount)) {
    errors.amount = "Enter a valid number.";
  } else if (type === "correction" ? amount === 0 : amount <= 0) {
    errors.amount = type === "correction" ? "Enter a non-zero amount." : "Enter a positive amount.";
  }

  const occurredDate = String(data.get("occurred_date") || "");
  if (!occurredDate) {
    errors.occurred_date = "Date is required.";
  } else if (Number.isNaN(new Date(`${occurredDate}T12:00:00`).getTime())) {
    errors.occurred_date = "Enter a valid date.";
  }

  if (!String(data.get("account_id") || "")) errors.account_id = "Account is required.";
  const accountId = String(data.get("account_id") || "");
  const transferAccountId = String(data.get("transfer_account_id") || "");
  if (type === "transfer" && !transferAccountId) {
    errors.transfer_account_id = "Destination account is required.";
  } else if (type === "transfer" && transferAccountId === accountId) {
    errors.transfer_account_id = "Choose a different destination.";
  }
  return errors;
}

export function hasRecordErrors(errors: RecordFormErrors) {
  return Boolean(errors.amount || errors.occurred_date || errors.account_id || errors.transfer_account_id);
}

export function recordDateLabel(value?: string | null) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== appToday().getFullYear() ? "numeric" : undefined,
  });
}
